import { generatePath, Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { RoutesData } from '@/router/RoutesData'
import type { AssistantCaseCard as CaseCardData } from '../contract'

export const assistantCasePath = (caseId: string) =>
  generatePath(RoutesData.RCase.path, { id: caseId })

/** "33% fee", or nothing when the fee is unknown. */
export const formatAssistantFee = (fee: number | null | undefined) =>
  typeof fee === 'number' ? `${fee}% fee` : null

const joinDetails = (parts: Array<string | null | undefined>) =>
  parts.filter(Boolean).join(' · ')

/**
 * One case as a compact bordered card (the design's answer cards): a bold
 * title, a short body and a small outlined "Open Case" button. Every field
 * comes from the structured result, never from the model's prose.
 */
const AssistantCaseCard = ({ data }: { data: CaseCardData }) => {
  const details = joinDetails([
    data.practiceArea,
    data.jurisdiction,
    formatAssistantFee(data.fee),
  ])
  const proposals =
    data.proposalCount && data.proposalCount > 0
      ? `${data.proposalCount} proposal${data.proposalCount === 1 ? '' : 's'}`
      : null
  const people = joinDetails([data.firm, proposals])

  return (
    <Box
      data-testid="assistant-case-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.5,
        p: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: data.needsAttention ? 'warning.main' : 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ overflowWrap: 'anywhere' }}
      >
        {data.title}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="p">
        {joinDetails([data.status, details])}
      </Typography>
      {people && (
        <Typography variant="caption" color="text.secondary" component="p">
          {people}
        </Typography>
      )}
      {data.summary && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {data.summary}
        </Typography>
      )}
      <Button
        component={RouterLink}
        to={assistantCasePath(data.caseId)}
        variant="outlined"
        size="small"
        data-testid="assistant-case-link"
        aria-label={`Open Case: ${data.title}`}
        sx={{ mt: 0.75, py: 0.25, px: 1.25, minWidth: 0 }}
      >
        Open Case
      </Button>
    </Box>
  )
}

export default AssistantCaseCard
